import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  PenSquare, Loader2, Search, User, Phone, ClipboardList, MessageCircle,
  Check, AlertCircle, X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const MODES = [
  { id: "app", label: "App patient", icon: User, hint: "Patients with a portal account" },
  { id: "intakeq", label: "IntakeQ client", icon: ClipboardList, hint: "Anyone in your IntakeQ roster" },
  { id: "spruce", label: "Spruce contact", icon: MessageCircle, hint: "Anyone in your Spruce contact list" },
  { id: "phone", label: "Manual phone", icon: Phone, hint: "Type a phone number directly" },
];

function fmtPhone(p) {
  if (!p) return "";
  const digits = (p || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return p;
}

function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}

export default function ProviderComposeDialog({ onSent }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("app");
  const [picked, setPicked] = useState(null); // {kind, ...}
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resultMsg, setResultMsg] = useState(null);
  const { toast } = useToast();

  const debounced = useDebouncedValue(search, 350);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMode("app");
      setPicked(null); setSearch(""); setResults([]); setSearching(false);
      setPhoneInput(""); setNameInput("");
      setSubject(""); setMessage("");
      setErrorMsg(""); setResultMsg(null); setSending(false);
    }
  }, [open]);

  // Search effect (mode-dependent)
  useEffect(() => {
    if (!open) return;
    if (mode === "phone") return;
    let cancelled = false;
    const run = async () => {
      setSearching(true);
      try {
        let r = [];
        if (mode === "app") {
          const { data } = await API.get(`/provider/app-users`, { params: { q: debounced, limit: 30 } });
          r = (data?.users || []).map(u => ({
            kind: "app",
            id: u.id,
            display_name: u.full_name || u.email,
            email: u.email,
            phone: u.phone || "",
            sub: u.email,
          }));
        } else if (mode === "intakeq") {
          if ((debounced || "").trim().length < 2) { r = []; }
          else {
            const { data } = await API.get(`/intakeq/clients`, { params: { q: debounced, limit: 25 } });
            r = (data?.clients || []).map(c => ({
              kind: "intakeq",
              id: c.intakeq_client_id,
              display_name: c.name || `${c.first_name} ${c.last_name}`.trim() || c.email || "(unnamed)",
              email: c.email || "",
              phone: c.phone || "",
              sub: [c.email, fmtPhone(c.phone)].filter(Boolean).join(" · "),
            }));
          }
        } else if (mode === "spruce") {
          const { data } = await API.get(`/spruce/contacts`, { params: { q: debounced, limit: 50 } });
          r = (data?.contacts || []).map(c => ({
            kind: "spruce",
            id: c.id,
            display_name: c.display_name,
            email: c.email || "",
            phone: c.phone || "",
            sub: [c.email, fmtPhone(c.phone)].filter(Boolean).join(" · ") || c.category,
          }));
        }
        if (!cancelled) setResults(r);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          // Show inline if Spruce isn't configured
          if (mode === "spruce" && err?.response?.status === 503) {
            setErrorMsg("Spruce API key isn't configured on the backend.");
          }
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [debounced, mode, open]);

  const canSend = useMemo(() => {
    if (!subject.trim() || !message.trim()) return false;
    if (mode === "phone") {
      return phoneInput.replace(/\D/g, "").length >= 10 && nameInput.trim().length >= 2;
    }
    return !!picked;
  }, [mode, picked, phoneInput, nameInput, subject, message]);

  const send = async () => {
    setSending(true);
    setErrorMsg("");
    setResultMsg(null);
    try {
      const payload = { mode, subject: subject.trim(), message: message.trim() };
      if (mode === "app") payload.app_user_id = picked.id;
      if (mode === "intakeq") {
        payload.intakeq_client_id = picked.id;
        payload.intakeq_email = picked.email;
        payload.intakeq_name = picked.display_name;
        payload.intakeq_phone = picked.phone;
      }
      if (mode === "spruce") {
        payload.spruce_contact_id = picked.id;
        payload.spruce_display_name = picked.display_name;
        payload.spruce_email = picked.email;
        payload.spruce_phone = picked.phone;
      }
      if (mode === "phone") {
        payload.phone = phoneInput;
        payload.name = nameInput || phoneInput;
      }
      const { data } = await API.post(`/provider/start-conversation`, payload);
      setResultMsg(data);
      toast({
        title: "Message sent",
        description: data.delivered_to_app_user
          ? "Delivered to the patient's portal inbox."
          : data.forwarded_to_spruce
            ? "Forwarded to Spruce for delivery."
            : "Saved. Spruce delivery may have failed — check the Spruce health pill.",
        duration: 4000,
      });
      onSent?.(data);
      // Auto-close after a short success state
      setTimeout(() => setOpen(false), 900);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || "Couldn't send the message. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full gap-1.5 h-9 text-xs" data-testid="provider-compose-button">
          <PenSquare className="w-3.5 h-3.5" /> New message
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="provider-compose-dialog">
        <DialogHeader>
          <DialogTitle>New message</DialogTitle>
          <DialogDescription>Start a thread with a patient on the portal, in IntakeQ, or by phone.</DialogDescription>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="grid grid-cols-2 gap-2">
          {MODES.map(m => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => { setMode(m.id); setPicked(null); setSearch(""); setResults([]); setErrorMsg(""); }}
                className={`text-left p-3 rounded-xl border transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}
                data-testid={`compose-mode-${m.id}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold">{m.label}</p>
                  {active && <Check className="w-3.5 h-3.5 text-primary ml-auto" />}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{m.hint}</p>
              </button>
            );
          })}
        </div>

        {/* Recipient picker */}
        {mode === "phone" ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Phone number (US)</Label>
              <Input
                data-testid="compose-phone-input"
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="h-10 mt-1"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-xs">Recipient name <span className="text-red-600">*</span></Label>
              <Input
                data-testid="compose-name-input"
                placeholder="Jane Doe"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="h-10 mt-1"
                required
                minLength={2}
              />
              {nameInput.trim().length > 0 && nameInput.trim().length < 2 && (
                <p className="text-[11px] text-red-600 mt-1">Please enter at least 2 characters.</p>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              The message will be sent via Spruce SMS to this number. The name is used to create the contact in Spruce if it doesn't exist yet. If they don't have a portal account, we'll auto-append a sign-up link.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="compose-search-input"
                placeholder={mode === "intakeq" ? "Type at least 2 letters of name or email…" : "Search by name, email, or phone…"}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPicked(null); }}
                className="pl-9 h-10"
              />
            </div>
            {picked ? (
              <div className="flex items-center justify-between gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20" data-testid="compose-picked-recipient">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{picked.display_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{picked.sub}</p>
                </div>
                <button onClick={() => setPicked(null)} className="p-1 rounded hover:bg-accent" aria-label="Clear">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="max-h-44 overflow-y-auto rounded-xl border border-border" data-testid="compose-results-list">
                {searching && (
                  <div className="p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                  </div>
                )}
                {!searching && results.length === 0 && (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    {mode === "intakeq" && (search || "").trim().length < 2
                      ? "Type at least 2 letters to search IntakeQ."
                      : "No matches."}
                  </div>
                )}
                {!searching && results.map(r => (
                  <button
                    key={`${r.kind}-${r.id}`}
                    onClick={() => setPicked(r)}
                    className="w-full text-left px-3 py-2.5 hover:bg-accent border-b border-border last:border-b-0"
                    data-testid="compose-result-item"
                  >
                    <p className="text-sm font-medium truncate">{r.display_name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{r.sub || "(no contact info)"}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subject + message */}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Subject</Label>
            <Input
              data-testid="compose-subject-input"
              placeholder="Quick check-in"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-10 mt-1"
              maxLength={120}
            />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              data-testid="compose-message-input"
              placeholder="Hi, just wanted to follow up on…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="mt-1 resize-none"
              maxLength={2000}
            />
          </div>
        </div>

        {errorMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="compose-error">
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMsg}</p>
          </div>
        )}

        {resultMsg && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2" data-testid="compose-success">
            <Check className="w-4 h-4 text-green-700 shrink-0 mt-0.5" />
            <p className="text-sm text-green-800">
              {resultMsg.delivered_to_app_user
                ? `Delivered to ${resultMsg.patient_name}'s portal inbox.`
                : resultMsg.forwarded_to_spruce
                  ? `Forwarded to Spruce for delivery to ${resultMsg.patient_name}.`
                  : `Saved, but Spruce delivery couldn't be confirmed.`}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={sending} data-testid="compose-cancel-button">
            Cancel
          </Button>
          <Button onClick={send} disabled={!canSend || sending} data-testid="compose-send-button" className="rounded-full">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
